import React from 'react';
import {
  Box,
  Container,
  SimpleGrid,
  Heading,
  Text,
  Image,
  Flex,
  Badge,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { FiMonitor, FiBriefcase, FiUserCheck, FiCpu, FiAward, FiCoffee } from 'react-icons/fi';

const FeaturesSection = () => {
  // 1. Define ALL dynamic values at the top level
  const bgMain = useColorModeValue("gray.50", "black");
  const cardBg = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const textMuted = useColorModeValue("gray.500", "gray.400");
  const headingColor = useColorModeValue("gray.800", "white");
  const iconBg = useColorModeValue("blue.50", "whiteAlpha.100");
  const badgeBg = useColorModeValue("whiteAlpha.900", "whiteAlpha.200");
  const badgeText = useColorModeValue("black", "white");
  
  const accentColor = "#0963ac";

  const featureData = [
    {
      title: "Live Classes",
      icon: FiMonitor,
      image: "https://i.pinimg.com/736x/df/a7/9a/dfa79af66cf07e118cd9c16c41a5e904.jpg",
      description: "Learn directly from industry pros with real-time feedback. Interactive sessions that bring the curriculum to life."
    },
    {
      title: "Real Client Projects",
      icon: FiBriefcase,
      image: "https://img.freepik.com/free-vector/businessmen-businesswoman-talking-desktop_1262-21452.jpg?size=626&ext=jpg&ga=GA1.1.136392163.1683044531&semt=ais",
      description: "Ship actual code for real clients. Experience the pressure and the thrill of professional environments."
    },
    {
      title: "Dedicated Mentors",
      icon: FiUserCheck,
      tag: "GuidanceGuru",
      image: "https://img.freepik.com/free-vector/bootcamp-job-training-illustration_23-2148751280.jpg?size=626&ext=jpg&ga=GA1.1.136392163.1683044531&semt=ais",
      description: "Personalized roadmaps and 1:1 support from experts who have walked the path before you."
    },
    {
      title: "Multiple Domains",
      icon: FiCpu,
      image: "https://img.freepik.com/free-vector/web-development-programmer-engineering-coding-website-augmented-reality-interface-screens-developer-project-engineer-programming-software-application-design-cartoon-illustration_107791-3863.jpg?size=626&ext=jpg&ga=GA1.1.136392163.1683044531&semt=ais",
      description: "Master Fullstack, Data, or Cloud. Build a versatile portfolio across the hottest tech stacks."
    },
    {
      title: "Global Certification",
      icon: FiAward,
      image: "https://img.freepik.com/free-vector/online-certification-concept_23-2148578825.jpg?size=626&ext=jpg&ga=GA1.1.136392163.1683044531&semt=ais",
      description: "Acknowledge your growth with tier-based certification that recruiters actually trust."
    },
    {
      title: "Team Bonding",
      icon: FiCoffee,
      tag: "Community",
      image: "https://img.freepik.com/free-vector/group-friends-sitting-table-talking-drinking-coffee-tea-tiny-people-friends-meeting-cheer-up-friend-friendship-support-concept_335657-623.jpg?size=626&ext=jpg&ga=GA1.1.136392163.1683044531&semt=ais",
      description: "Culture isn't just code. Bond with your cohort over team lunches and networking mixers."
    }
  ];

  return (
    <Box py={24} bg={bgMain}>
      <Container maxW="7xl">
        <Box mb={16} textAlign="left">
          <Badge colorScheme="blue" variant="subtle" px={3} py={1} borderRadius="full" mb={4}>
            The GrapplTech Way
          </Badge>
          <Heading fontFamily="Bricolage Grotesque" size="2xl" mb={4} fontWeight="800" color={headingColor}>
            More than just a bootcamp.
          </Heading>
          <Text fontSize="xl" color={textMuted} maxW="2xl">
            We provide the tools, the projects, and the network to turn you into a high-signal engineer.
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10}>
          {featureData.map((item, index) => (
            <Box
              key={index}
              role="group"
              bg={cardBg}
              borderRadius="3xl"
              p={2}
              border="1px solid"
              borderColor={borderColor}
              transition="all 0.4s cubic-bezier(.175,.885,.32,1.275)"
              _hover={{ 
                transform: "translateY(-8px)",
                boxShadow: "2xl",
                borderColor: accentColor
              }}
            >
              <Box position="relative" h="200px" overflow="hidden" borderRadius="2xl">
                {item.tag && (
                  <Badge
                    position="absolute"
                    top={4}
                    right={4}
                    zIndex={10}
                    bg={badgeBg}
                    color={badgeText}
                    borderRadius="full"
                    px={3}
                  >
                    {item.tag}
                  </Badge>
                )}
                <Image
                  src={item.image}
                  w="full"
                  h="full"
                  objectFit="cover"
                  transition="transform 0.5s ease"
                  _groupHover={{ transform: "scale(1.1)" }}
                />
                <Box 
                  position="absolute" 
                  inset="0" 
                  bgGradient="linear(to-t, blackAlpha.600, transparent)" 
                />
              </Box>

              <Box p={6}>
                <Flex align="center" mb={4}>
                  <Flex 
                    p={2} 
                    bg={iconBg} 
                    color={accentColor} 
                    borderRadius="lg" 
                    mr={3}
                  >
                    <Icon as={item.icon} boxSize={5} />
                  </Flex>
                  <Heading size="md" fontWeight="700" color={headingColor}>
                    {item.title}
                  </Heading>
                </Flex>
                
                <Text fontSize="md" color={textMuted} lineHeight="tall">
                  {item.description}
                </Text>
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default FeaturesSection;